import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';
import { DatePickerComponent } from '@components/date-picker/date-picker.component';
import { MsfInput } from '@components/ui/forms/input/input';
import { MsfSelect } from '@components/select/select';
import {
  QUARTZ_SCHEDULE_KINDS,
  QUARTZ_WEEKDAYS,
  QuartzScheduleDraft,
  QuartzScheduleKind,
  QuartzWeekday,
  buildQuartzCron,
  formatQuartzCronLabel,
  nextQuartzOccurrence,
  parseQuartzCron,
} from '@app/pages/admin/orchestration-quartz-cron.util';
import { buildTimeZoneOptions, formatTimeZoneLabel } from '@app/pages/admin/orchestration-time-zones.util';

@Component({
  selector: 'app-quartz-schedule-fields',
  standalone: true,
  imports: [FormsModule, TranslateModule, DatePickerComponent, MsfInput, MsfSelect],
  templateUrl: './quartz-schedule-fields.html',
  styleUrl: './quartz-schedule-fields.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuartzScheduleFieldsComponent {
  private readonly translate = inject(TranslateService);
  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(
      map(event => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  readonly cronExpression = model.required<string>();
  readonly timeZoneId = model.required<string>();
  readonly validChange = output<boolean>();
  readonly offsetError = signal(false);
  readonly offsetInput = signal<number | null>(0);

  private readonly draft = signal<QuartzScheduleDraft>(parseQuartzCron('0 0 6 * * ?'));

  readonly kindOptions = QUARTZ_SCHEDULE_KINDS.map(item => ({ ...item }));
  readonly weekdayOptions = QUARTZ_WEEKDAYS.map(item => ({ ...item }));
  readonly hourIntervalOptions = [2, 3, 4, 6, 8, 12].map(hours => ({
    id: hours,
    label: String(hours),
  }));
  readonly monthDayOptions = Array.from({ length: 31 }, (_, index) => ({
    id: index + 1,
    label: String(index + 1),
  }));

  readonly kind = computed(() => this.draft().kind);
  readonly everyHours = computed(() => this.draft().everyHours);
  readonly weekday = computed(() => this.draft().weekday);
  readonly monthDay = computed(() => this.draft().monthDay);
  readonly customExpression = computed(() => this.draft().customExpression);
  readonly showOffset = computed(() => this.kind() === 'hourly');
  readonly showEveryHours = computed(() => this.kind() === 'everyHours');
  readonly showWeekday = computed(() => this.kind() === 'weekly');
  readonly showMonthDay = computed(() => this.kind() === 'monthly');
  readonly showTime = computed(() => {
    const kind = this.kind();
    return kind === 'daily' || kind === 'weekdays' || kind === 'weekly' || kind === 'monthly';
  });
  readonly showCustomExpression = computed(() => this.kind() === 'custom');
  readonly timeValue = computed(() => {
    const value = new Date();
    value.setHours(this.draft().hour, this.draft().minute, 0, 0);
    return value;
  });
  readonly timeZoneOptions = computed(() => buildTimeZoneOptions(new Date(), [this.timeZoneId()]));
  readonly summary = computed(() => {
    this.lang();
    return formatQuartzCronLabel(this.cronExpression(), (key, params) => this.translate.instant(key, params));
  });
  readonly timeZoneSummary = computed(() => formatTimeZoneLabel(this.timeZoneId() || 'UTC'));
  readonly nextRunLabel = computed(() => {
    this.lang();
    const next = nextQuartzOccurrence(this.cronExpression(), this.timeZoneId());
    if (!next) {
      return '';
    }
    try {
      const formatted = new Intl.DateTimeFormat(this.translate.currentLang || 'en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: this.timeZoneId().trim() || 'UTC',
      }).format(next);
      return this.translate.instant('Quartz next run', { time: formatted });
    } catch {
      return '';
    }
  });

  constructor() {
    effect(() => this.validChange.emit(!this.showOffset() || !this.offsetError()));
    effect(() => {
      const expression = this.cronExpression().trim();
      const current = untracked(() => this.draft());
      if (buildQuartzCron(current) === expression) {
        return;
      }
      this.draft.set(parseQuartzCron(expression));
      this.offsetInput.set(this.draft().minute);
      this.offsetError.set(false);
    });
  }

  onOffsetChange(value: number | string | null): void {
    const minute = value === null || String(value).trim() === '' ? NaN : Number(value);
    const valid = Number.isInteger(minute) && minute >= 0 && minute <= 59;
    this.offsetInput.set(Number.isFinite(minute) ? minute : null);
    this.offsetError.set(!valid);
    if (valid) {
      this.patchDraft({ minute });
    }
  }

  onKindChange(kind: QuartzScheduleKind): void {
    if (kind !== this.kind()) {
      this.offsetError.set(false);
      if (kind === 'hourly') {
        this.offsetInput.set(0);
        this.patchDraft({ kind, minute: 0 });
        return;
      }
    }
    if (kind === 'custom') {
      this.patchDraft({ kind, customExpression: buildQuartzCron(this.draft()) });
      return;
    }
    this.patchDraft({ kind });
  }

  onTimeChange(value: Date | null): void {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return;
    }
    this.patchDraft({ hour: value.getHours(), minute: value.getMinutes() });
  }

  onEveryHoursChange(everyHours: number): void {
    this.patchDraft({ everyHours });
  }

  onWeekdayChange(weekday: QuartzWeekday): void {
    this.patchDraft({ weekday });
  }

  onMonthDayChange(monthDay: number): void {
    this.patchDraft({ monthDay });
  }

  onCustomExpressionChange(customExpression: string): void {
    this.patchDraft({ customExpression });
  }

  private patchDraft(patch: Partial<QuartzScheduleDraft>): void {
    const next = { ...this.draft(), ...patch };
    this.draft.set(next);
    this.cronExpression.set(buildQuartzCron(next));
  }
}
